import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Activity, Menu, Zap, ShieldCheck, Wrench, MessageSquareCode, LayoutDashboard, BookText, LogOut, LogIn } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useMode } from "../context/ModeContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/scanner", label: "System Scanner", icon: ShieldCheck, testid: "nav-scanner" },
  { to: "/assistant", label: "AI Assistant", icon: MessageSquareCode, testid: "nav-assistant" },
  { to: "/troubleshooter", label: "Troubleshooter", icon: Wrench, testid: "nav-troubleshooter" },
  { to: "/knowledge-base", label: "Knowledge Base", icon: BookText, testid: "nav-knowledge-base" },
];

const ModeToggle = () => {
  const { proMode, setProMode } = useMode();
  return (
    <div className="flex items-center gap-2.5" data-testid="mode-toggle-wrap">
      <span className={`text-xs font-mono uppercase tracking-widest ${!proMode ? "text-primary" : "text-muted-foreground"}`}>
        User
      </span>
      <Switch checked={proMode} onCheckedChange={setProMode} data-testid="mode-toggle" />
      <span className={`text-xs font-mono uppercase tracking-widest ${proMode ? "text-primary" : "text-muted-foreground"}`}>
        IT Pro
      </span>
    </div>
  );
};

const AuthControls = () => {
  const { user, loading, login, logout } = useAuth();
  if (loading) return <div className="h-9 w-9 rounded-full bg-secondary/60 animate-pulse" />;
  if (!user) {
    return (
      <Button variant="outline" onClick={login}
        className="border-border/70 rounded-lg" data-testid="login-btn">
        <LogIn className="h-4 w-4 mr-1.5" /> Sign in
      </Button>
    );
  }
  const initials = (user.name || user.email || "U").slice(0, 2).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-full ring-1 ring-border/70 hover:ring-primary/50 transition" data-testid="user-menu-trigger">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-primary/15 text-primary text-xs font-bold">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="glass border-border/70 w-56">
        <DropdownMenuLabel className="truncate">
          <p className="font-semibold truncate" data-testid="user-name">{user.name}</p>
          <p className="text-xs text-muted-foreground truncate font-normal">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-red-400 focus:text-red-400 cursor-pointer" data-testid="logout-btn">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
      isActive
        ? "bg-primary/15 text-primary"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
    }`;

  return (
    <header className="fixed top-0 inset-x-0 h-20 z-50 glass border-b border-border/60">
      <div className="max-w-7xl mx-auto h-full px-5 sm:px-6 flex items-center justify-between gap-4">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-3 shrink-0"
          data-testid="brand-logo"
        >
          <div className="relative h-10 w-10 rounded-xl bg-primary/15 border border-primary/30 grid place-items-center glow-blue">
            <Activity className="h-5 w-5 text-primary" />
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 pulse-dot ring-2 ring-background" />
          </div>
          <div className="text-left leading-none">
            <p className="font-heading font-extrabold text-lg tracking-tight">OmniFix AI</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mt-1">
              Diagnostic Hub
            </p>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkClass} data-testid={n.testid}>
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="hidden md:block">
            <ModeToggle />
          </div>
          <Button
            onClick={() => navigate("/scanner")}
            className="hidden sm:flex bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg"
            data-testid="header-quick-scan-btn"
          >
            <Zap className="h-4 w-4 mr-1.5" /> Quick Scan
          </Button>

          <AuthControls />

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="glass border-border/60 w-72">
              <SheetTitle className="font-heading text-xl mb-6">Menu</SheetTitle>
              <SheetDescription className="sr-only">Main navigation menu</SheetDescription>
              <div className="flex flex-col gap-1">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    className={linkClass}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${n.testid}`}
                  >
                    <n.icon className="h-4 w-4" />
                    {n.label}
                  </NavLink>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-border/60">
                <ModeToggle />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
